import { isArray } from "lodash-es";
import type { ValidatorFn } from "./forms";

type InputValue = string | number | boolean | null | string[];


export const runValidations = (value: InputValue, validators: ValidatorFn[] = []): string[] => {
    const errors: string[] = [];

    for (const validateFn of validators) {
        const result = validateFn(value);
        if (result) errors.push(result);
    }

    return errors;
};

function withType<T>(fn: ValidatorFn<T>, type: string): ValidatorFn<T> {
    fn.__type = type;
    return fn;
}

export const Validators = {
    required: withType(
        (val: InputValue): string | null =>
            (!val || (isArray(val) && !val.length) ? 'Campo obrigatório.' : null),
        'required'
    ),

    minLength: (minLength: number): ValidatorFn =>
        withType(
            (val: string) =>
                val && val.length < minLength
                    ? `Mínimo de ${minLength} caracteres.`
                    : null,
            'minLength'
        ),

    maxLength: (maxLength: number): ValidatorFn =>
        withType(
            (val: string) =>
                val && val.length > maxLength
                    ? `Máximo de ${maxLength} caracteres.`
                    : null,
            'maxLength'
        ),

    max: (max: number): ValidatorFn =>
        withType(
            (val: number) =>
                val > max ? `O valor não pode ser maior que ${max}.` : null,
            'max'
        ),

    min: (min: number): ValidatorFn =>
        withType(
            (val: number) =>
                val < min ? `O valor não pode ser menor que ${min}.` : null,
            'min'
        ),

    email: withType(
        (val: string) => {
            if (!val) return null;
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailRegex.test(val) ? null : 'Email inválido.';
        },
        'email'
    ),

    // Valida se o texto é um regex
    isRegex: (): ValidatorFn =>
        withType(
            (val: string) => {
                if (!val) return null;

                // Bloqueia regex no formato /.../
                if (/^\/.*\/$/.test(val)) {
                    return 'Regex inválido';
                }

                try {
                    new RegExp(val); // nosemgrep -- só valida a sintaxe do regex digitado pelo admin; roda no navegador, não no servidor
                    return null; // regex válido
                } catch {
                    return 'Regex inválido';
                }
            },
            'isRegex'
        ),

    // Valida se o texto atende o regex
    regex: (regex: string, message: string): ValidatorFn =>
        withType(
            (val: string) => {
                if (!val) return null;

                try {
                    return new RegExp(regex).test(val) ? null : message; // nosemgrep -- regex vem da config do campo (admin autenticado); roda no navegador, não no servidor
                } catch (err) {
                    return "O regex está em formato inválido"
                }
            },
            'regex'
        ),

    // Valida nome completo: ao menos duas palavras, somente letras, cada uma com no mínimo 3 letras
    fullName: withType(
        (val: string) => {
            if (!val) return null;

            const words = val.trim().split(/\s+/);

            if (words.length < 2) return 'Informe nome e sobrenome.';
            if (!words.every(word => /^\p{L}+$/u.test(word))) return 'O nome deve conter somente letras.';
            if (!words.every(word => word.length >= 3)) return 'Cada palavra deve ter no mínimo 3 letras.';

            return null;
        },
        'fullName'
    ),

    cpf: withType(
        (val: string) => {
            if (!val) return null;
            const cpf = val.replace(/[^\d]/g, '');

            if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return 'CPF inválido.';

            let sum = 0;
            for (let i = 0; i < 9; i++) sum += parseInt(cpf[i]) * (10 - i);
            let firstCheck = (sum * 10) % 11;
            if (firstCheck >= 10) firstCheck = 0;
            if (firstCheck !== parseInt(cpf[9])) return 'CPF inválido.';

            sum = 0;
            for (let i = 0; i < 10; i++) sum += parseInt(cpf[i]) * (11 - i);
            let secondCheck = (sum * 10) % 11;
            if (secondCheck >= 10) secondCheck = 0;
            if (secondCheck !== parseInt(cpf[10])) return 'CPF inválido.';

            return null;
        },
        'cpf'
    ),

    cnpj: withType(
        (val: string) => {
            if (!val) return null;
            const cleaned = val.toUpperCase().replace(/[^0-9A-Z]/g, '');
            if (cleaned.length !== 14 || /^(.)\1+$/.test(cleaned)) return 'CNPJ inválido.';

            const body = cleaned.slice(0, 12);
            const dv = cleaned.slice(12);

            const charValue = (ch: string) =>
                /[0-9]/.test(ch) ? parseInt(ch) : ch.charCodeAt(0) - 48;

            const calc = (base: string, weights: number[]) => {
                const sum = base
                    .split('')
                    .reduce((acc, ch, i) => acc + charValue(ch) * weights[i], 0);
                const mod = sum % 11;
                return mod < 2 ? 0 : 11 - mod;
            };

            const w1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
            const w2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

            const d1 = calc(body, w1);
            const d2 = calc(body + d1.toString(), w2);

            return dv === `${d1}${d2}` ? null : 'CNPJ inválido.';
        },
        'cnpj'
    )
};